/* Heimatgut soft market/language popup.
   Detects the visitor's country (IP geolocation, GDPR-conscious — one cached call
   to a no-logging endpoint, with a navigator.languages fallback that sends no IP)
   and, if the suggested language differs from the one currently shown, slides up a
   branded bottom-corner card suggesting the switch. Never hard-redirects; remembers
   the visitor's choice. The actual switch is a native {% form 'localization' %} POST. */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return; // don't nag in the theme editor

  var DECISION_KEY = 'heimat-market-decision';
  var GEO_KEY = 'heimat-geo-country';
  var DAY = 86400000;

  function readJSON(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeJSON(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
  }

  function getConfig() {
    var el = document.querySelector('[data-heimat-market-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function suggestedFromCountry(cfg, code) {
    if (code && cfg.map && cfg.map[code]) return cfg.map[code];
    return cfg.fallback;
  }
  function suggestedFromLanguage(cfg) {
    var langs = navigator.languages || [navigator.language || ''];
    for (var i = 0; i < langs.length; i++) {
      var base = (langs[i] || '').toLowerCase().split('-')[0];
      if (base && cfg.available.indexOf(base) !== -1) return base;
      if (base === 'de' && cfg.available.indexOf('de') !== -1) return 'de';
    }
    return cfg.fallback;
  }

  // Tracking consent (Shopify Customer Privacy API). userCanBeTracked() is true
  // when the visitor is outside a consent-required region OR has granted consent,
  // so the geo lookup keeps working where no consent is legally required and stays
  // off until consent is given where it is.
  function consentSaysYes() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.userCanBeTracked === 'function') return !!cp.userCanBeTracked();
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) {}
    return false;
  }
  function geoConsentGranted(cb) {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) { cb(consentSaysYes()); return; }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) { cb(err ? false : consentSaysYes()); }
        );
        return;
      }
    } catch (e) {}
    cb(false); // no consent API available → don't send the IP
  }

  // Resolve { suggested, countryName } then hand off to render.
  function resolve(cfg, done) {
    var cached = readJSON(GEO_KEY);
    if (cached && cached.ts && (Date.now() - cached.ts) < 30 * DAY) {
      done(suggestedFromCountry(cfg, cached.code), cached.name || '');
      return;
    }
    if (!cfg.geoUrl) { done(suggestedFromLanguage(cfg), ''); return; }

    // GDPR/TDDDG: the geo lookup sends the visitor IP to a third party, so it only
    // runs once tracking consent is granted. Without consent we use the
    // navigator.languages signal, which sends no IP anywhere.
    geoConsentGranted(function (allowed) {
      if (!allowed) { done(suggestedFromLanguage(cfg), ''); return; }

      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, 3500);
      fetch(cfg.geoUrl, { signal: controller.signal, mode: 'cors', credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function (d) {
          clearTimeout(timer);
          var code = String(d.country || d.country_code || '').toUpperCase();
          var name = d.name || d.country_name || '';
          if (code) writeJSON(GEO_KEY, { code: code, name: name, ts: Date.now() });
          done(suggestedFromCountry(cfg, code), name);
        })
        .catch(function () {
          clearTimeout(timer);
          done(suggestedFromLanguage(cfg), ''); // no IP leaves the browser on this path
        });
    });
  }

  function fill(el, sel, text) {
    var node = el.querySelector(sel);
    if (node) node.textContent = text;
  }

  function render(cfg, suggested, countryName) {
    if (!suggested || cfg.available.indexOf(suggested) === -1) return;
    if (suggested === cfg.current) return; // already showing the right language

    var copy = cfg.copy[suggested];
    if (!copy) return;
    var popup = document.querySelector('[data-heimat-market-popup]');
    if (!popup) return;

    var body = countryName
      ? (copy.body || copy.bodyPlain || '').replace('{country}', countryName)
      : (copy.bodyPlain || copy.body || '').replace('{country}', '');
    var stay = (copy.stay || '').replace('{current}', cfg.currentName || '');

    fill(popup, '[data-heimat-mp-heading]', copy.heading || '');
    fill(popup, '[data-heimat-mp-body]', body);
    fill(popup, '[data-heimat-mp-switch-label]', copy.switch || '');
    fill(popup, '[data-heimat-mp-stay]', stay);

    var localeInput = popup.querySelector('[data-heimat-mp-locale]');
    if (localeInput) localeInput.value = suggested;

    show(popup, cfg);
  }

  function show(popup, cfg) {
    var lastFocus = document.activeElement;
    popup.hidden = false;
    // next frame so the transition runs
    requestAnimationFrame(function () { popup.classList.add('is-visible'); });

    var switchBtn = popup.querySelector('[data-heimat-mp-switch]');
    var stayBtn = popup.querySelector('[data-heimat-mp-stay-btn]');
    var closeBtn = popup.querySelector('[data-heimat-mp-close]');
    var focusables = [switchBtn, stayBtn, closeBtn].filter(Boolean);

    function record(decision) { writeJSON(DECISION_KEY, { decision: decision, ts: Date.now() }); }

    function dismiss() {
      record('dismissed');
      popup.classList.remove('is-visible');
      document.removeEventListener('keydown', onKey, true);
      window.setTimeout(function () { popup.hidden = true; }, 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); dismiss(); return; }
      if (e.key !== 'Tab' || focusables.length === 0) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    if (switchBtn) switchBtn.addEventListener('click', function () { record('chosen'); }); // form then submits natively
    if (stayBtn) stayBtn.addEventListener('click', dismiss);
    if (closeBtn) closeBtn.addEventListener('click', dismiss);
    document.addEventListener('keydown', onKey, true);

    if (switchBtn) requestAnimationFrame(function () { switchBtn.focus(); });
  }

  function start() {
    var cfg = getConfig();
    if (!cfg || !cfg.available || cfg.available.length < 2) return;

    var decision = readJSON(DECISION_KEY);
    var remember = (cfg.rememberDays || 30) * DAY;
    if (decision && decision.ts && (Date.now() - decision.ts) < remember) return;

    // Visitor already on a non-default locale → they've localized; don't nag.
    if (cfg.current !== cfg.primary) return;

    resolve(cfg, function (suggested, countryName) { render(cfg, suggested, countryName); });
  }

  function boot() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 2500 });
    } else {
      window.setTimeout(start, 1200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
